import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Award, ShieldCheck, Dna, Activity, MapPin, Mail, Phone, ExternalLink, Calendar, ChevronRight, Download, FileText, Users, Gavel, Presentation, Globe } from "lucide-react";

import { ASSETS, ARTICULOS, PILARES, VALORES, RAZAS, GALERIA, CLUB_INFO, FASES_MATRIZ, CRITERIOS, LAZ_INFO, LAZ_RESPONSABILIDADES, CAMADAS } from "@/content";
import { NAV, HERO, SECTION, EVENT, DIRECTIVA, CONTACTO } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* =================================================================
   NAV
================================================================= */
const NAV_ITEMS = [
  { id: "inicio",    label: "Inicio",     testId: NAV.inicio },
  { id: "club",      label: "El Club",    testId: NAV.club },
  { id: "filosofia", label: "Filosofía",  testId: NAV.filosofia },
  { id: "crianza",   label: "Crianza",    testId: NAV.crianza },
  { id: "razas",     label: "Razas",      testId: NAV.razas },
  { id: "camadas",   label: "Camadas",    testId: "nav-link-camadas" },
  { id: "directiva", label: "Directiva",  testId: NAV.directiva },
  { id: "laz",       label: "LAZ",        testId: "nav-link-laz" },
  { id: "eventos",   label: "Eventos",    testId: NAV.eventos },
  { id: "contacto",  label: "Contacto",   testId: NAV.contacto },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-rsv-black/90 backdrop-blur-md border-b border-rsv-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
        <a href="#inicio" className="flex items-center gap-3 group" data-testid={NAV.brand}>
          <img
            src={ASSETS.logo}
            alt="RSV Global Guayaquil"
            className="h-12 w-auto object-contain"
            style={{ filter: "drop-shadow(0 0 8px rgba(0,168,89,0.45))" }}
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black text-rsv-ivory text-[15px] tracking-tight">RSV<span className="text-rsv-green">global</span></span>
            <span className="font-mono text-[10px] tracking-institutional text-rsv-muted uppercase mt-0.5">Guayaquil · Ecuador</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              data-testid={item.testId}
              className="font-mono text-[11px] uppercase tracking-institutional text-rsv-bone hover:text-rsv-green transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <button
          aria-label="Toggle menu"
          className="lg:hidden text-rsv-ivory"
          onClick={() => setOpen(!open)}
          data-testid={NAV.mobileToggle}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden bg-rsv-black border-t border-rsv-line px-5 py-6 flex flex-col gap-5">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              data-testid={`${item.testId}-mobile`}
              onClick={() => setOpen(false)}
              className="font-mono text-sm uppercase tracking-institutional text-rsv-bone hover:text-rsv-green"
            >
              {item.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
}

/* =================================================================
   HERO
================================================================= */
function Hero() {
  return (
    <section id="inicio" data-testid={HERO.section} className="relative min-h-screen pt-32 pb-20 overflow-hidden bg-rsv-charcoal">
      {/* Background grain + radial */}
      <div className="absolute inset-0 grid-noise opacity-40 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 30% 20%, rgba(0,168,89,0.15) 0%, transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(20,224,127,0.08) 0%, transparent 60%)",
        }}
      />

      {/* Top scanner bar */}
      <div className="absolute top-24 left-0 right-0 h-px bg-rsv-line-strong">
        <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-rsv-green to-transparent animate-scan" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-rsv-green animate-pulse-dot" />
            <span className="font-mono text-[11px] tracking-institutional uppercase text-rsv-green">
              Reg. RSVglobal · Hann. Münden 24.04.2025
            </span>
          </div>

          <h1 className="font-display font-black uppercase leading-[0.9] tracking-[-0.02em] text-rsv-ivory text-[clamp(2.4rem,5.6vw,4.8rem)]">
            Filosofía de<br />
            <span className="text-rsv-green">protección genética</span><br />
            <span className="font-serif-doc italic font-medium normal-case text-rsv-bone">del perro de trabajo.</span>
          </h1>

          <div className="mt-10 max-w-xl">
            <p className="font-body text-base md:text-lg leading-relaxed text-rsv-bone">
              El <span className="text-rsv-ivory font-semibold">RSVglobal Guayaquil</span> opera en Ecuador como miembro cooperativo del <a href={CLUB_INFO.globalUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-rsv-green underline-offset-4 hover:text-rsv-green">Schäferhundverein RSVglobal e.V.</a>, heredero institucional del RSV2000 fundado por el Dr. Helmut Raiser en 2007 con un único cometido: preservar al Pastor Alemán y al Malinois como <em className="text-rsv-green not-italic">perros de utilidad</em> (Gebrauchshunde).
            </p>
            <p className="font-body text-sm md:text-base text-rsv-muted mt-5 leading-relaxed">
              Aplicamos la matriz oficial RSV2000 (Edición 2024) en su integridad. Anatomía funcional, adaptabilidad social, temperamento, instintos y capacidad de trabajo definen el orden de evaluación; ningún criterio estético los sustituye ni los precede.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#filosofia"
              data-testid={HERO.cta}
              className="group inline-flex items-center gap-3 bg-rsv-green text-rsv-black px-7 py-4 font-mono text-[11px] uppercase tracking-institutional font-semibold hover:bg-rsv-ivory transition-colors duration-200"
            >
              Doctrina del Club
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a
              href="#eventos"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center gap-3 border border-rsv-line-strong text-rsv-ivory px-7 py-4 font-mono text-[11px] uppercase tracking-institutional hover:border-rsv-green hover:text-rsv-green transition-colors"
            >
              Ver Eventos
            </a>
          </div>

          {/* Lema */}
          <div className="mt-14 pt-8 border-t border-rsv-line flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] tracking-institutional uppercase text-rsv-muted">
            <span className="text-rsv-gold">Disciplina</span><span className="opacity-30">/</span>
            <span className="text-rsv-gold">Pasión</span><span className="opacity-30">/</span>
            <span className="text-rsv-gold">Resultados</span>
          </div>
        </div>

        {/* Right: visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[3/4] border border-rsv-line-strong bg-rsv-black overflow-hidden">
            <img
              src={ASSETS.marlonAction}
              alt="Working line dog in heel"
              className="w-full h-full object-cover object-center"
              style={{ filter: "contrast(1.08) saturate(0.85)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rsv-black via-transparent to-transparent" />

            {/* Caption block */}
            <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-rsv-line-strong bg-rsv-black/80 backdrop-blur-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-1">REG / Trabajo Real</p>
                  <p className="font-display font-bold uppercase text-rsv-ivory text-sm">Heeling — Malinois</p>
                </div>
                <span className="font-mono text-[10px] text-rsv-muted">GYE · ECU</span>
              </div>
            </div>
          </div>

          {/* Decorative side label */}
          <div className="absolute -left-4 top-12 hidden lg:block font-mono text-[10px] tracking-institutional uppercase text-rsv-muted" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            RSVglobal · est. crianza línea de trabajo · GYE
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-rsv-line bg-rsv-black/60 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-4 font-mono text-[11px] tracking-institutional uppercase text-rsv-muted">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0 gap-12 px-6">
              <span>· Erhalt des Kulturguts</span>
              <span className="text-rsv-green">· Gebrauchshundezucht</span>
              <span>· Pool genético amplio</span>
              <span className="text-rsv-green">· Función sobre belleza</span>
              <span>· Doctrina Helmut Raiser</span>
              <span className="text-rsv-green">· BH · IGP · SchH</span>
              <span>· Erbe Max von Stephanitz</span>
              <span className="text-rsv-green">· Back to basics</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
   SOBRE EL CLUB + URKUNDE
================================================================= */
function SectionClub() {
  return (
    <section id="club" data-testid={SECTION.club} className="relative py-28 bg-rsv-black border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="01" overline="Sobre el Club" />

        <div className="grid lg:grid-cols-12 gap-12 mt-12">
          <div className="lg:col-span-7">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Filial cooperativa,<br />
              <span className="text-rsv-green">vinculación doctrinal.</span>
            </h2>

            <div className="mt-10 space-y-6 max-w-2xl font-body text-rsv-bone leading-relaxed">
              <p>
                El <strong className="text-rsv-ivory">RSVglobal Guayaquil</strong> es miembro cooperativo del <strong className="text-rsv-ivory">Schäferhundverein RSVglobal e.V.</strong>, con sede en Hann. Münden y registro VR 201744 ante el Amtsgericht Göttingen. Su matriz, el RSV2000, fue fundada el 23 de junio de 2007 por iniciativa del Dr. Helmut Raiser para sostener efectivamente al Pastor Alemán como bien cultural cinotécnico (Kulturgut). En 2022 amplió su tutela al Pastor Belga.
              </p>
              <p className="text-rsv-muted">
                Bajo la marca <em className="text-rsv-green not-italic">RSVglobal</em> — representación cooperativa internacional del RSV2000 — el club aplica en Ecuador idénticos instrumentos: matriz de evaluación, plan de crianza, pruebas oficiales y el principio rector del retorno a los fundamentos cinotécnicos.
              </p>
              <p>
                El RSVglobal no es una asociación comercial. No emite, fomenta ni intermedia camadas con fines de mercado. Cada ejemplar bajo nuestra tutela se evalúa por <span className="text-rsv-gold">anatomía funcional, adaptabilidad social, temperamento, instintos y capacidad de trabajo</span>: únicamente cuando estos rasgos se documentan y se demuestran, procede hablar de crianza.
              </p>
            </div>

            {/* Values strip */}
            <div className="mt-12 grid grid-cols-3 sm:grid-cols-6 gap-px bg-rsv-line border border-rsv-line">
              {VALORES.map((v) => (
                <div key={v} className="bg-rsv-black px-3 py-5 text-center">
                  <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold">{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* URKUNDE — typographic recreation */}
          <div className="lg:col-span-5">
            <div className="relative border border-rsv-line-strong bg-rsv-ivory p-1">
              <div className="bg-white p-8 md:p-10 relative">
                {/* Top accent — three vertical bars in green (echoing the original document) */}
                <div className="absolute top-8 left-8 flex gap-1 h-24">
                  <span className="w-[3px] h-full bg-rsv-green-deep" />
                  <span className="w-[3px] h-3/4 bg-rsv-green-deep" />
                  <span className="w-[3px] h-1/2 bg-rsv-green-deep" />
                </div>

                <div className="ml-12">
                  <div className="flex items-center justify-between mb-8 pb-4 border-b border-black/10">
                    <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green-deep font-bold">Urkunde · Aval cooperativo</span>
                    <Award size={18} className="text-rsv-green-deep" />
                  </div>

                  {/* Logo */}
                  <div className="flex justify-center mb-8">
                    <img
                      src={ASSETS.logo}
                      alt="RSVglobal Guayaquil"
                      className="h-28 w-auto object-contain"
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-center font-display font-black uppercase tracking-[0.4em] text-rsv-black text-2xl md:text-3xl">
                    Urkunde
                  </h3>

                  {/* Body */}
                  <div className="mt-8 text-center font-body text-black text-[15px] leading-relaxed">
                    <p>Der RSVglobal Guayaquil</p>
                    <p>ist kooperatives Mitglied des</p>
                    <p className="mt-2 font-display font-bold uppercase tracking-tight text-xl">RSV2000</p>
                  </div>

                  {/* Signature */}
                  <div className="mt-12 pt-3 border-t border-black/20 max-w-[240px]">
                    <p className="font-serif-doc italic text-xl text-rsv-black leading-tight">Dr. Raiser</p>
                    <p className="mt-2 font-mono text-[10px] tracking-institutional uppercase text-black/70 leading-snug">
                      Dr. Helmut Raiser<br />
                      Präsident RSVglobal<br />
                      Hann. Münden, 24.04.2025
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">
              Recreación tipográfica del acta original · firmada por la Presidencia
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =================================================================
   FILOSOFIA — 10 ARTICLES
================================================================= */
function SectionFilosofia() {
  return (
    <section id="filosofia" data-testid={SECTION.filosofia} className="relative py-28 bg-rsv-charcoal border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="02" overline="Filosofía Doctrinal · Diez Artículos" />

        <div className="grid lg:grid-cols-12 gap-12 mt-10 mb-16">
          <div className="lg:col-span-6">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Los diez<br />
              <span className="text-rsv-green">artículos.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="font-serif-doc italic text-rsv-bone text-lg leading-relaxed max-w-md">
              &ldquo;El Pastor Alemán es un bien cultural cinotécnico; su preservación como perro de utilidad obliga a una doctrina antes que a una técnica.&rdquo;
              <span className="block mt-3 font-mono not-italic text-[10px] tracking-institutional uppercase text-rsv-muted">— Schäferhundverein RSV2000 e.V. · 2007</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rsv-line border border-rsv-line">
          {ARTICULOS.map((a, i) => (
            <motion.article
              key={a.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.05 }}
              className="bg-rsv-charcoal p-7 md:p-9 flex gap-6 hover:bg-rsv-green-darker transition-colors duration-300 group"
              data-testid={`articulo-${a.n}`}
            >
              <span className="font-mono font-bold text-rsv-green text-3xl md:text-4xl leading-none shrink-0 group-hover:text-rsv-gold transition-colors">
                {a.n}
              </span>
              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-rsv-ivory text-sm md:text-base mb-2">{a.titulo}</h3>
                <p className="font-body text-rsv-bone text-sm leading-relaxed">{a.texto}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
   PLAN DE CRIANZA
================================================================= */
function SectionCrianza() {
  return (
    <section id="crianza" data-testid={SECTION.crianza} className="relative py-28 bg-rsv-black border-t border-rsv-line overflow-hidden">
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(0,168,89,0.18) 0%, transparent 55%)" }}
      />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="03" overline="Plan de Crianza · Matriz RSV2000" />

        <div className="grid lg:grid-cols-12 gap-12 mt-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Cinco pilares.<br />
              <span className="text-rsv-green">Una sola puerta.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-body text-rsv-bone text-base leading-relaxed">
              Cada ejemplar candidato a apto de cría se confronta con la matriz documentada del RSV2000. Sin la verificación íntegra de los cinco pilares, ninguna reproducción es avalada por el club.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-rsv-line border border-rsv-line">
          {PILARES.map((p) => (
            <div key={p.n} className="bg-rsv-charcoal p-7 flex flex-col justify-between min-h-[260px] group hover:bg-rsv-green-darker transition-colors duration-300">
              <div className="flex items-start justify-between">
                <span className="font-serif-doc text-rsv-green text-5xl leading-none">{p.n}</span>
                <PillarIcon pillar={p.n} />
              </div>
              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-rsv-ivory text-sm mb-3">{p.titulo}</h3>
                <p className="font-body text-rsv-bone text-[13px] leading-relaxed">{p.texto}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold font-bold">[ Handbook · Matriz Oficial RSV2000 · Edición 2024 ]</span>
            <span className="h-px flex-1 bg-rsv-line-strong" />
          </div>

          <p className="font-body text-rsv-bone max-w-3xl text-base leading-relaxed mb-10">
            La <strong className="text-rsv-ivory">Matriz para la Cría y el Adiestramiento</strong> (Handbook LCC, RSV2000 © Edición 2024) es el instrumento técnico que rige la evaluación de cada ejemplar a lo largo de su vida, desde la observación temprana hasta la confirmación del apto de cría. Su aplicación es íntegra e idéntica en Alemania y en Ecuador.
          </p>

          {/* Phases timeline */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rsv-line border border-rsv-line">
            {FASES_MATRIZ.map((f) => (
              <div key={f.n} className="bg-rsv-charcoal p-7 group hover:bg-rsv-green-darker transition-colors duration-300" data-testid={`fase-${f.n}`}>
                <div className="flex items-baseline justify-between mb-4">
                  <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green font-bold">FASE {f.n}</span>
                  <span className="font-mono text-[10px] uppercase text-rsv-muted">{f.edad}</span>
                </div>
                <h4 className="font-display font-bold uppercase tracking-tight text-rsv-ivory text-base mb-4">{f.fase}</h4>
                <ul className="space-y-2">
                  {f.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 font-body text-[13px] text-rsv-bone leading-snug">
                      <span className="text-rsv-gold mt-1.5 shrink-0">·</span> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Scoring legend */}
          <div className="mt-10 border border-rsv-line p-7 md:p-9 bg-rsv-charcoal">
            <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold mb-5">Sistema de Puntuación · 1 / 3 / 5</p>
            <div className="grid sm:grid-cols-3 gap-px bg-rsv-line border border-rsv-line">
              <div className="bg-rsv-black p-5">
                <span className="font-mono font-bold text-rsv-muted text-3xl">1</span>
                <p className="font-body text-rsv-bone text-xs mt-2 leading-snug">Eliminatorio · disposición incompatible con la crianza de trabajo.</p>
              </div>
              <div className="bg-rsv-black p-5">
                <span className="font-mono font-bold text-rsv-bone text-3xl">3</span>
                <p className="font-body text-rsv-bone text-xs mt-2 leading-snug">Adecuado · cumple el estándar mínimo, sin destacar.</p>
              </div>
              <div className="bg-rsv-black p-5">
                <span className="font-mono font-bold text-rsv-green text-3xl">5</span>
                <p className="font-body text-rsv-bone text-xs mt-2 leading-snug">Excelente · expresión equilibrada y deseable del rasgo evaluado.</p>
              </div>
            </div>
          </div>

          {/* Criteria families */}
          <div className="mt-12">
            <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-6">Familias de Criterios Evaluados</p>
            <div className="grid md:grid-cols-2 gap-px bg-rsv-line border border-rsv-line">
              {CRITERIOS.map((c) => (
                <div key={c.titulo} className="bg-rsv-charcoal p-6 flex gap-5">
                  <ShieldCheck size={18} className="text-rsv-green shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-display font-bold uppercase text-rsv-ivory text-sm tracking-tight mb-1">{c.titulo}</h5>
                    <p className="font-body text-rsv-bone text-xs leading-relaxed">{c.detalle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* PDF downloads */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <a
              href={ASSETS.matrizGSD}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-matriz-gsd"
              className="group flex items-center justify-between gap-6 border border-rsv-line p-6 md:p-7 bg-rsv-charcoal hover:border-rsv-green hover:bg-rsv-green-darker transition-all"
            >
              <div className="flex items-center gap-5">
                <FileText size={28} className="text-rsv-green shrink-0" />
                <div>
                  <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">Documento Oficial · PDF</p>
                  <p className="font-display font-bold uppercase text-rsv-ivory text-sm md:text-base tracking-tight mt-1">Matriz · Pastor Alemán</p>
                  <p className="font-body text-rsv-bone text-xs mt-1">Handbook completo · Edición 2024</p>
                </div>
              </div>
              <Download size={20} className="text-rsv-line-strong group-hover:text-rsv-green transition-colors shrink-0" />
            </a>

            <a
              href={ASSETS.matrizMalinois}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-matriz-malinois"
              className="group flex items-center justify-between gap-6 border border-rsv-line p-6 md:p-7 bg-rsv-charcoal hover:border-rsv-green hover:bg-rsv-green-darker transition-all"
            >
              <div className="flex items-center gap-5">
                <FileText size={28} className="text-rsv-green shrink-0" />
                <div>
                  <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">Documento Oficial · PDF</p>
                  <p className="font-display font-bold uppercase text-rsv-ivory text-sm md:text-base tracking-tight mt-1">Matriz · Malinois</p>
                  <p className="font-body text-rsv-bone text-xs mt-1">Handbook completo · Edición 2024</p>
                </div>
              </div>
              <Download size={20} className="text-rsv-line-strong group-hover:text-rsv-green transition-colors shrink-0" />
            </a>
          </div>

          <p className="mt-6 font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">
            © Schäferhundverein RSVglobal e.V. · Edición 2024 · Aplicado íntegramente por la filial cooperativa de Guayaquil
          </p>
        </div>
      </div>
    </section>
  );
}

function PillarIcon({ pillar }) {
  const cls = "text-rsv-gold/80 group-hover:text-rsv-gold transition-colors";
  switch (pillar) {
    case "I":   return <Activity size={20} className={cls} />;
    case "II":  return <ShieldCheck size={20} className={cls} />;
    case "III": return <Award size={20} className={cls} />;
    case "IV":  return <Dna size={20} className={cls} />;
    case "V":   return <Activity size={20} className={cls} />;
    default:    return null;
  }
}

/* =================================================================
   RAZAS
================================================================= */
function SectionRazas() {
  return (
    <section id="razas" data-testid={SECTION.razas} className="relative py-28 bg-rsv-charcoal border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="04" overline="Razas Bajo Tutela" />

        <h2 className="mt-10 font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)] max-w-3xl">
          Dos razas.<br />
          <span className="text-rsv-green">Un solo principio: utilidad.</span>
        </h2>

        <div className="mt-16 grid md:grid-cols-2 gap-px bg-rsv-line border border-rsv-line">
          {RAZAS.map((r, i) => (
            <article key={r.nombre} className="bg-rsv-black flex flex-col group" data-testid={`raza-${i}`}>
              <div className="relative aspect-[4/3] overflow-hidden border-b border-rsv-line">
                <img
                  src={r.img}
                  alt={r.nombre}
                  className="w-full h-full object-cover object-center grayscale group-hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rsv-black/60 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-mono text-[10px] tracking-institutional uppercase text-rsv-green bg-rsv-black/70 backdrop-blur-sm px-3 py-1.5 border border-rsv-green/40">
                  REG · 0{i + 1}
                </span>
              </div>
              <div className="p-7 md:p-9">
                <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted mb-3">{r.sub}</p>
                <h3 className="font-display font-black uppercase text-rsv-ivory text-2xl md:text-3xl tracking-tight mb-4">{r.nombre}</h3>
                <p className="font-body text-rsv-bone text-sm leading-relaxed">{r.descripcion}</p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {r.rasgos.map((rasgo) => (
                    <li key={rasgo} className="font-mono text-[10px] tracking-institutional uppercase border border-rsv-line-strong px-3 py-1.5 text-rsv-bone">
                      {rasgo}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
   CAMADAS
================================================================= */
function SectionCamadas() {
  return (
    <section
      id="camadas"
      data-testid="section-camadas"
      className="relative py-28 bg-rsv-black border-t border-rsv-line overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at 30% 10%, rgba(0,168,89,0.14) 0%, transparent 55%)" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="05" overline="Camadas · Línea Genealógica" />

        <div className="grid lg:grid-cols-12 gap-12 mt-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Más que un origen,<br />
              <span className="text-rsv-green">una base para el futuro.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-body text-rsv-bone text-base leading-relaxed">
              Cruces planificados con líneas policiales de <strong className="text-rsv-ivory">Francia, Holanda y Bélgica</strong>. Cada camada nace con destino de trabajo — no de mascota. La colocación exige entrevista, contrato y confirmación de uso funcional.
            </p>
          </div>
        </div>

        {CAMADAS.map((c) => (
          <article key={c.id} className="mb-16" data-testid={`camada-${c.id}`}>
            {/* Camada header row */}
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-rsv-line">
              <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold bg-rsv-black border border-rsv-gold/40 px-3 py-1.5">
                {c.codigo}
              </span>
              <div className="flex-1 min-w-[200px]">
                <h3 className="font-display font-black uppercase tracking-tight text-rsv-ivory text-xl md:text-2xl">
                  {c.titulo}
                </h3>
                <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted mt-1">
                  {c.subtitulo}
                </p>
              </div>
              <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green bg-rsv-green-darker border border-rsv-green/50 px-3 py-1.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-rsv-green animate-pulse-dot" />
                {c.estado}
              </span>
            </div>

            {/* Composite pattern — single full infographic */}
            {c.composite && (
              <div className="grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-8 order-2 lg:order-1">
                  <a
                    href={c.composite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative border border-rsv-line-strong overflow-hidden group bg-rsv-black"
                    data-testid={`camada-composite-${c.id}`}
                  >
                    <img
                      src={c.composite}
                      alt={`Infografía oficial — ${c.titulo}`}
                      className="w-full h-auto object-contain"
                      loading="lazy"
                    />
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-ivory bg-rsv-black/80 backdrop-blur-sm px-3 py-1.5 border border-rsv-green">
                        Ver en tamaño completo ↗
                      </span>
                    </div>
                  </a>
                </div>

                <div className="lg:col-span-4 order-1 lg:order-2 lg:sticky lg:top-24 space-y-6">
                  {c.lema && (
                    <p className="font-serif-doc italic text-rsv-gold text-lg leading-snug">
                      &ldquo;{c.lema}&rdquo;
                    </p>
                  )}
                  {c.padres && c.padres.map((p, idx) => (
                    <div key={idx} className="border-t border-rsv-line pt-5">
                      <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-2">
                        [ Progenitor {idx + 1} ]
                      </p>
                      <h4 className="font-display font-bold uppercase text-rsv-ivory text-base tracking-tight mb-2">
                        {p.nombre}
                      </h4>
                      <p className="font-mono text-[11px] text-rsv-bone leading-relaxed mb-1">
                        {p.origen}
                      </p>
                      <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">
                        {p.criador}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Parents pattern — separate infographics per parent */}
            {c.parents && (
              <div className="grid gap-10">
                {c.parents.map((p, idx) => (
                  <div key={idx} className="grid lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-8 order-2 lg:order-1">
                      <a
                        href={p.infografia}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block relative border border-rsv-line-strong overflow-hidden group bg-rsv-black"
                        data-testid={`camada-infografia-${idx}`}
                      >
                        <img
                          src={p.infografia}
                          alt={`Infografía genealógica — ${p.nombre}`}
                          className="w-full h-auto object-contain"
                          loading="lazy"
                        />
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-ivory bg-rsv-black/80 backdrop-blur-sm px-3 py-1.5 border border-rsv-green">
                            Ver en tamaño completo ↗
                          </span>
                        </div>
                      </a>
                    </div>

                    <div className="lg:col-span-4 order-1 lg:order-2 lg:sticky lg:top-24">
                      <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-3">
                        [ {p.rol} ]
                      </p>
                      <h4 className="font-display font-black uppercase leading-tight text-rsv-ivory text-2xl md:text-3xl tracking-tight">
                        {p.nombre}
                      </h4>
                      <p className="font-mono text-[11px] tracking-institutional uppercase text-rsv-bone mt-3">
                        {p.subtitulo}
                      </p>
                      <div className="mt-6 pt-6 border-t border-rsv-line space-y-4 font-mono text-[11px]">
                        <div>
                          <p className="text-[9px] tracking-institutional uppercase text-rsv-muted mb-1">Raza</p>
                          <p className="text-rsv-ivory">{p.raza}</p>
                        </div>
                        <div>
                          <p className="text-[9px] tracking-institutional uppercase text-rsv-muted mb-1">Origen</p>
                          <p className="text-rsv-ivory">{p.origen}</p>
                        </div>
                      </div>
                      <p className="mt-6 font-serif-doc italic text-rsv-gold text-lg leading-snug">
                        &ldquo;{p.lema}&rdquo;
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reservation notice */}
            <div className="mt-16 border border-rsv-green/40 bg-rsv-green-darker p-8 md:p-10">
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold mb-3">
                    [ Condiciones de Reserva y Colocación ]
                  </p>
                  <h4 className="font-display font-black uppercase text-rsv-ivory text-2xl md:text-3xl tracking-tight leading-tight mb-6">
                    No es una venta.<br />Es una <span className="text-rsv-green">colocación filtrada.</span>
                  </h4>
                  <ul className="space-y-3">
                    {c.condiciones.map((cond) => (
                      <li key={cond} className="flex items-start gap-3 font-body text-rsv-bone text-sm leading-relaxed">
                        <span className="text-rsv-green mt-1.5 shrink-0">▸</span>
                        {cond}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5 flex flex-col items-start gap-4">
                  <a
                    href="#contacto"
                    data-testid={`camada-cta-reserva-${c.id}`}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-rsv-green text-rsv-black px-7 py-4 font-mono text-[11px] uppercase tracking-institutional font-semibold hover:bg-rsv-ivory transition-colors"
                  >
                    Solicitar entrevista
                    <ArrowUpRight size={16} />
                  </a>
                  <a
                    href={CLUB_INFO.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 border border-rsv-ivory/40 text-rsv-ivory px-7 py-4 font-mono text-[11px] uppercase tracking-institutional hover:border-rsv-green hover:text-rsv-green transition-colors"
                  >
                    WhatsApp Directiva
                    <Phone size={14} />
                  </a>
                  <p className="mt-2 font-mono text-[9px] tracking-institutional uppercase text-rsv-muted leading-relaxed">
                    Sólo destino de trabajo. La directiva se reserva el derecho de rechazar cualquier solicitud sin justificar motivo.
                  </p>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* =================================================================
   DIRECTIVA — split into Mesa Directiva + Cuerpo Técnico
================================================================= */
function InitialsAvatar({ nombre }) {
  const initials = nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
  return (
    <div className="w-full h-full flex items-center justify-center bg-rsv-green-darker relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{ background: "radial-gradient(circle at 50% 30%, rgba(0,168,89,0.4) 0%, transparent 60%)" }}
      />
      <span className="font-display font-black text-rsv-green text-[clamp(3rem,7vw,6rem)] tracking-tight relative z-10">{initials}</span>
      <span className="absolute bottom-3 left-3 right-3 font-mono text-[9px] tracking-institutional uppercase text-rsv-muted z-10 text-center">
        Retrato pendiente
      </span>
    </div>
  );
}

function DirectivaCard({ m }) {
  return (
    <article
      key={m.id}
      className="bg-rsv-charcoal flex flex-col group"
      data-testid={DIRECTIVA.card(m.id)}
    >
      <div className="relative aspect-[3/4] overflow-hidden border-b border-rsv-line">
        {m.foto_url ? (
          <img
            src={m.foto_url}
            alt={m.nombre}
            className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
          />
        ) : (
          <InitialsAvatar nombre={m.nombre} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-rsv-black/70 via-transparent to-transparent pointer-events-none" />
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-institutional uppercase text-rsv-gold bg-rsv-black/70 backdrop-blur-sm px-3 py-1.5 border border-rsv-gold/40">
          {m.registro}
        </span>
        {m.cargo_en && (
          <span className="absolute top-4 right-4 font-mono text-[9px] tracking-institutional uppercase text-rsv-bone bg-rsv-black/70 backdrop-blur-sm px-3 py-1.5 border border-rsv-line-strong">
            {m.cargo_en}
          </span>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-display font-black uppercase text-rsv-ivory text-lg tracking-tight">{m.nombre}</h3>
        <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mt-1 mb-4">{m.cargo}</p>
        {m.bio && (
          <p className="font-body text-rsv-bone text-[13px] leading-relaxed mb-5">{m.bio}</p>
        )}

        {(m.email || m.telefono || m.ciudad) && (
          <div className="mt-auto pt-4 border-t border-rsv-line space-y-1.5 font-mono text-[11px]">
            {m.ciudad && (
              <p className="flex items-center gap-2 text-rsv-bone">
                <MapPin size={11} className="text-rsv-muted shrink-0" /> {m.ciudad}
              </p>
            )}
            {m.telefono && (
              <a href={`https://wa.me/${m.telefono.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-rsv-bone hover:text-rsv-green transition-colors">
                <Phone size={11} className="text-rsv-muted shrink-0" /> {m.telefono}
              </a>
            )}
            {m.email && (
              <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-rsv-bone hover:text-rsv-green transition-colors break-all">
                <Mail size={11} className="text-rsv-muted shrink-0" /> {m.email}
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function SectionDirectiva({ directiva }) {
  const mesa = directiva.filter((m) => (m.categoria || "directiva") === "directiva");
  const tecnicos = directiva.filter((m) => m.categoria === "tecnico");

  return (
    <section id="directiva" data-testid={SECTION.directiva} className="relative py-28 bg-rsv-black border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="06" overline="Directiva del Club · Registro Oficial" />

        <div className="grid lg:grid-cols-12 gap-12 mt-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Quienes<br /><span className="text-rsv-green">sostienen el club.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-body text-rsv-bone text-base leading-relaxed">
              Mesa directiva oficial inscrita ante el Schäferhundverein RSVglobal e.V., Hann. Münden. Cada cargo es público, verificable y opera bajo el reglamento del club matriz.
            </p>
          </div>
        </div>

        {directiva.length === 0 ? (
          <p className="font-mono text-rsv-muted text-sm">Cargando registro…</p>
        ) : (
          <>
            {/* Mesa Directiva */}
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold font-bold">[ Mesa Directiva ]</span>
              <span className="h-px flex-1 bg-rsv-line-strong" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-rsv-line border border-rsv-line">
              {mesa.map((m) => <DirectivaCard key={m.id} m={m} />)}
            </div>

            {/* Cuerpo Técnico */}
            {tecnicos.length > 0 && (
              <div className="mt-16">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold font-bold">[ Cuerpo Técnico ]</span>
                  <span className="h-px flex-1 bg-rsv-line-strong" />
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-rsv-line border border-rsv-line">
                  {tecnicos.map((m) => <DirectivaCard key={m.id} m={m} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/* =================================================================
   LAZ — Landesausbildungswart · Authority Section
================================================================= */
function LAZIcon({ name }) {
  const cls = "text-rsv-green shrink-0";
  switch (name) {
    case "shield":       return <ShieldCheck size={22} className={cls} />;
    case "users":        return <Users size={22} className={cls} />;
    case "gavel":        return <Gavel size={22} className={cls} />;
    case "award":        return <Award size={22} className={cls} />;
    case "presentation": return <Presentation size={22} className={cls} />;
    case "globe":        return <Globe size={22} className={cls} />;
    default:             return <ShieldCheck size={22} className={cls} />;
  }
}

function SectionLAZ({ titularFoto }) {
  return (
    <section
      id="laz"
      data-testid="section-laz"
      className="relative py-28 bg-rsv-charcoal border-t border-rsv-line overflow-hidden"
    >
      {/* Forest atmosphere background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background: "radial-gradient(ellipse at 80% 20%, rgba(0,168,89,0.18) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(20,224,127,0.10) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="07" overline="Autoridad Técnica · Landesausbildungswart" />

        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-12 mt-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] tracking-institutional uppercase text-rsv-gold mb-4">
              [ Nombramiento Oficial · {LAZ_INFO.jurisdiccion} ]
            </p>
            <h2 className="font-display font-black uppercase leading-[0.92] tracking-tight text-rsv-ivory text-[clamp(2.4rem,5.4vw,4.4rem)]">
              El rol del<br />
              <span className="text-rsv-green">{LAZ_INFO.acronimo}.</span>
            </h2>
            <p className="mt-6 font-mono text-[11px] tracking-institutional uppercase text-rsv-bone">
              {LAZ_INFO.alemania}
            </p>
          </div>
          <div className="lg:col-span-5">
            <p className="font-body text-rsv-bone text-base leading-relaxed">
              {LAZ_INFO.definicion}
            </p>
          </div>
        </div>

        {/* Frank's portrait + significado */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-4">
            <div className="relative aspect-[3/4] border border-rsv-line-strong overflow-hidden bg-rsv-black">
              {titularFoto ? (
                <img
                  src={titularFoto}
                  alt={LAZ_INFO.titular}
                  className="w-full h-full object-cover object-top"
                  style={{ filter: "contrast(1.05) saturate(0.9)" }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-mono text-rsv-muted text-xs">RETRATO PENDIENTE</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-rsv-black via-rsv-black/30 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-rsv-line-strong bg-rsv-black/85 backdrop-blur-sm">
                <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-1">
                  Titular en ejercicio
                </p>
                <p className="font-display font-black uppercase text-rsv-ivory text-base tracking-tight leading-tight">
                  {LAZ_INFO.titular}
                </p>
                <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold mt-1">
                  Cuenca · Ecuador
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 flex flex-col justify-center">
            <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-gold mb-6">
              ¿Qué significa ser LAZ?
            </p>
            <p className="font-serif-doc italic text-rsv-bone text-xl md:text-2xl leading-relaxed">
              {LAZ_INFO.significado}
            </p>
            <div className="mt-8 pt-6 border-t border-rsv-line">
              <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted leading-relaxed">
                Working Dog Education · Breeding · Sport<br />
                Estructura superior: Schäferhundverein RSVglobal e.V. — RSV2000 Alemania
              </p>
            </div>
          </div>
        </div>

        {/* Responsibilities grid */}
        <div className="flex items-center gap-4 mb-8">
          <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green font-bold">
            [ Responsabilidades Principales ]
          </span>
          <span className="h-px flex-1 bg-rsv-line-strong" />
          <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">
            06 puntos
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rsv-line border border-rsv-line">
          {LAZ_RESPONSABILIDADES.map((r, i) => (
            <motion.article
              key={r.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
              className="bg-rsv-black p-7 md:p-8 group hover:bg-rsv-green-darker transition-colors duration-300 flex flex-col"
              data-testid={`laz-resp-${r.n}`}
            >
              <div className="flex items-start justify-between mb-5">
                <span className="font-serif-doc text-rsv-green text-4xl leading-none">{r.n}</span>
                <LAZIcon name={r.icon} />
              </div>
              <h3 className="font-display font-bold uppercase text-rsv-ivory text-sm tracking-tight mb-3">
                {r.titulo}
              </h3>
              <p className="font-body text-rsv-bone text-[13px] leading-relaxed">
                {r.texto}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
   EVENTOS
================================================================= */
function SectionEventos({ eventos }) {
  const [filter, setFilter] = useState("todos");

  const visibles = filter === "todos" ? eventos : eventos.filter((e) => e.estado === filter);

  const ESTADO_LABEL = {
    proximo:  { label: "PRÓXIMO",  color: "text-rsv-green" },
    en_curso: { label: "EN CURSO", color: "text-rsv-gold" },
    pasado:   { label: "PASADO",   color: "text-rsv-muted" },
  };

  return (
    <section id="eventos" data-testid={SECTION.eventos} className="relative py-28 bg-rsv-charcoal border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="08" overline="Registro Oficial · Pruebas y Eventos de Trabajo" />

        <div className="grid lg:grid-cols-12 gap-12 mt-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Lo que viene.<br /><span className="text-rsv-green">Lo que pasó.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-2">
            <FilterButton testId={EVENT.filterAll} active={filter === "todos"} onClick={() => setFilter("todos")}>Todos</FilterButton>
            <FilterButton testId={EVENT.filterProximo} active={filter === "proximo"} onClick={() => setFilter("proximo")}>Próximos</FilterButton>
            <FilterButton testId={EVENT.filterPasado} active={filter === "pasado"} onClick={() => setFilter("pasado")}>Pasados</FilterButton>
          </div>
        </div>

        {visibles.length === 0 ? (
          <p className="font-mono text-rsv-muted text-sm">Sin eventos en esta categoría.</p>
        ) : (
          <div className="border-t border-rsv-line">
            {visibles.map((e) => {
              const est = ESTADO_LABEL[e.estado] || ESTADO_LABEL.pasado;
              return (
                <article
                  key={e.id}
                  data-testid={EVENT.row(e.id)}
                  className="grid grid-cols-12 gap-4 py-7 border-b border-rsv-line hover:bg-rsv-green-darker transition-colors duration-300 group cursor-pointer"
                >
                  <div className="col-span-12 md:col-span-2">
                    <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">{e.registro}</p>
                    <p className={`font-mono text-[10px] tracking-institutional uppercase mt-1 font-bold ${est.color}`}>● {est.label}</p>
                  </div>
                  <div className="col-span-12 md:col-span-2">
                    <p className="font-mono text-xs text-rsv-ivory">{formatDate(e.fecha_inicio)}</p>
                    {e.fecha_fin && e.fecha_fin !== e.fecha_inicio && (
                      <p className="font-mono text-[10px] text-rsv-muted">→ {formatDate(e.fecha_fin)}</p>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-display font-bold uppercase text-rsv-ivory text-base md:text-lg tracking-tight group-hover:text-rsv-green transition-colors">
                      {e.titulo}
                    </h3>
                    <p className="font-body text-rsv-bone text-sm mt-1 leading-relaxed">{e.descripcion}</p>
                  </div>
                  <div className="col-span-6 md:col-span-2 font-mono text-[11px] text-rsv-bone">
                    <p className="text-rsv-muted text-[9px] tracking-institutional uppercase mb-1">Ubicación</p>
                    <p className="flex items-center gap-1.5"><MapPin size={12} className="text-rsv-green" /> {e.ubicacion}</p>
                    {e.ponente && <p className="text-rsv-muted text-[10px] mt-2">Ponente: <span className="text-rsv-ivory">{e.ponente}</span></p>}
                  </div>
                  <div className="col-span-6 md:col-span-1 flex md:justify-end items-start">
                    <ArrowUpRight size={20} className="text-rsv-line-strong group-hover:text-rsv-green transition-colors" />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function FilterButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`font-mono text-[11px] uppercase tracking-institutional px-5 py-2.5 border transition-colors ${
        active ? "border-rsv-green bg-rsv-green text-rsv-black" : "border-rsv-line-strong text-rsv-bone hover:border-rsv-green hover:text-rsv-green"
      }`}
    >
      {children}
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

/* =================================================================
   GALERIA
================================================================= */
function SectionGaleria() {
  return (
    <section id="galeria" data-testid={SECTION.galeria} className="relative py-28 bg-rsv-black border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="09" overline="Galería · Trabajo Real" />

        <h2 className="mt-10 mb-12 font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)] max-w-3xl">
          La cría<br /><span className="text-rsv-green">se demuestra en el campo.</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-rsv-line border border-rsv-line">
          {GALERIA.map((g, i) => (
            <figure
              key={i}
              className="relative bg-rsv-charcoal aspect-square overflow-hidden group"
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105 ease-out"
                style={g.objectPosition ? { objectPosition: g.objectPosition } : undefined}
                loading="lazy"
              />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
   CONTACTO
================================================================= */
function SectionContacto() {
  const [form, setForm] = useState({ nombre: "", email: "", asunto: "", mensaje: "" });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback(null);
    try {
      await axios.post(`${API}/contacto`, form);
      setFeedback({ type: "ok", msg: "Mensaje enviado. La directiva responderá en breve." });
      setForm({ nombre: "", email: "", asunto: "", mensaje: "" });
    } catch (err) {
      setFeedback({ type: "err", msg: "No fue posible enviar el mensaje. Intenta por WhatsApp." });
    }
    setLoading(false);
  };

  return (
    <section id="contacto" data-testid={SECTION.contacto} className="relative py-28 bg-rsv-charcoal border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel index="10" overline="Contacto · Directiva" />

        <div className="grid lg:grid-cols-12 gap-16 mt-12">
          <div className="lg:col-span-5">
            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tight text-rsv-ivory text-[clamp(2rem,4.2vw,3.5rem)]">
              Comunicación<br /><span className="text-rsv-green">con la directiva.</span>
            </h2>
            <p className="font-body text-rsv-bone text-base leading-relaxed mt-8 max-w-md">
              Para solicitudes de inscripción, evaluaciones bajo matriz, acceso al handbook técnico y consultas relativas a la crianza de líneas de trabajo. La directiva responde de forma directa.
            </p>

            <div className="mt-12 space-y-6 font-mono text-sm">
              <a
                href={CLUB_INFO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={CONTACTO.whatsapp}
                className="flex items-center gap-4 group"
              >
                <span className="w-10 h-10 border border-rsv-green flex items-center justify-center group-hover:bg-rsv-green transition-colors">
                  <Phone size={16} className="text-rsv-green group-hover:text-rsv-black transition-colors" />
                </span>
                <div>
                  <p className="text-[10px] tracking-institutional uppercase text-rsv-muted">WhatsApp · Directiva</p>
                  <p className="text-rsv-ivory mt-0.5 group-hover:text-rsv-green transition-colors">{CLUB_INFO.whatsapp}</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <span className="w-10 h-10 border border-rsv-line-strong flex items-center justify-center">
                  <MapPin size={16} className="text-rsv-green" />
                </span>
                <div>
                  <p className="text-[10px] tracking-institutional uppercase text-rsv-muted">Ubicación</p>
                  <p className="text-rsv-ivory mt-0.5">{CLUB_INFO.ciudad}</p>
                </div>
              </div>

              <a
                href={CLUB_INFO.globalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
              >
                <span className="w-10 h-10 border border-rsv-line-strong flex items-center justify-center group-hover:border-rsv-green transition-colors">
                  <ExternalLink size={16} className="text-rsv-green" />
                </span>
                <div>
                  <p className="text-[10px] tracking-institutional uppercase text-rsv-muted">Sitio Internacional</p>
                  <p className="text-rsv-ivory mt-0.5 group-hover:text-rsv-green transition-colors">rsvglobal.live</p>
                </div>
              </a>

              <a
                href={CLUB_INFO.parentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
              >
                <span className="w-10 h-10 border border-rsv-line-strong flex items-center justify-center group-hover:border-rsv-green transition-colors">
                  <ExternalLink size={16} className="text-rsv-green" />
                </span>
                <div>
                  <p className="text-[10px] tracking-institutional uppercase text-rsv-muted">Club Matriz · Alemania</p>
                  <p className="text-rsv-ivory mt-0.5 group-hover:text-rsv-green transition-colors">rsv2000.de</p>
                </div>
              </a>
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid={CONTACTO.form} className="lg:col-span-7 border border-rsv-line p-7 md:p-10 bg-rsv-black">
            <div className="grid sm:grid-cols-2 gap-6">
              <FormField label="Nombre" name="nombre" value={form.nombre} onChange={onChange} testId={CONTACTO.nombre} required />
              <FormField label="E-mail" name="email" type="email" value={form.email} onChange={onChange} testId={CONTACTO.email} required />
            </div>
            <div className="mt-6">
              <FormField label="Asunto" name="asunto" value={form.asunto} onChange={onChange} testId={CONTACTO.asunto} required />
            </div>
            <div className="mt-6">
              <label className="block font-mono text-[10px] tracking-institutional uppercase text-rsv-muted mb-2">Mensaje</label>
              <textarea
                name="mensaje"
                value={form.mensaje}
                onChange={onChange}
                rows={5}
                required
                data-testid={CONTACTO.mensaje}
                className="w-full bg-rsv-charcoal border border-rsv-line-strong px-4 py-3 font-body text-rsv-ivory text-sm focus:outline-none focus:border-rsv-green transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid={CONTACTO.submit}
              className="mt-8 inline-flex items-center gap-3 bg-rsv-green text-rsv-black px-7 py-4 font-mono text-[11px] uppercase tracking-institutional font-semibold hover:bg-rsv-ivory transition-colors disabled:opacity-50"
            >
              {loading ? "Enviando…" : "Enviar mensaje"}
              <ArrowUpRight size={16} />
            </button>

            {feedback && (
              <p
                data-testid={CONTACTO.feedback}
                className={`mt-5 font-mono text-xs ${feedback.type === "ok" ? "text-rsv-green" : "text-red-400"}`}
              >
                {feedback.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, name, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="block font-mono text-[10px] tracking-institutional uppercase text-rsv-muted mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full bg-rsv-charcoal border border-rsv-line-strong px-4 py-3 font-body text-rsv-ivory text-sm focus:outline-none focus:border-rsv-green transition-colors"
      />
    </div>
  );
}

/* =================================================================
   FOOTER
================================================================= */
function Footer() {
  return (
    <footer className="bg-rsv-black border-t border-rsv-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img src={ASSETS.logo} alt="RSV Global" className="h-16 w-auto object-contain" />
              <div>
                <p className="font-display font-black text-rsv-ivory text-lg leading-none">RSV<span className="text-rsv-green">global</span> Guayaquil</p>
                <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-muted mt-1">Club Deportivo de Trabajo y Utilidad Canina</p>
              </div>
            </div>
            <p className="font-body text-rsv-bone text-sm mt-6 max-w-md leading-relaxed">
              Miembro cooperativo del Schäferhundverein RSVglobal e.V. — Alemania. Crianza, adiestramiento y demostración deportiva de las líneas de trabajo del Pastor Alemán y del Pastor Belga, conforme a la doctrina cinotécnica del RSV2000.
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-4">Navegación</p>
            <ul className="space-y-2 font-mono text-xs">
              {NAV_ITEMS.slice(1).map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-rsv-bone hover:text-rsv-green transition-colors">{item.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green mb-4">Vínculos institucionales</p>
            <ul className="space-y-3 font-mono text-xs">
              <li>
                <a href={CLUB_INFO.globalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-rsv-bone hover:text-rsv-green transition-colors">
                  RSVglobal e.V. — Internacional <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <a href={CLUB_INFO.parentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-rsv-bone hover:text-rsv-green transition-colors">
                  RSV2000 e.V. Alemania <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <a href={CLUB_INFO.whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-rsv-bone hover:text-rsv-green transition-colors">
                  WhatsApp Directiva <ExternalLink size={11} />
                </a>
              </li>
            </ul>
            <p className="mt-6 font-mono text-[10px] tracking-institutional uppercase text-rsv-muted leading-relaxed">
              {CLUB_INFO.registro}<br />Berliner Str. 70 · D-34346 Hann. Münden
            </p>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-rsv-line flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-mono text-[10px] tracking-institutional uppercase text-rsv-muted">
          <p>© {new Date().getFullYear()} RSVglobal Guayaquil · Todos los derechos reservados</p>
          <p>Hann. Münden · Guayaquil · Reg. 24.04.2025</p>
        </div>
      </div>
    </footer>
  );
}

/* =================================================================
   SHARED: SectionLabel
================================================================= */
function SectionLabel({ index, overline }) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-green font-bold">[ {index} ]</span>
      <span className="h-px w-12 bg-rsv-line-strong" />
      <span className="font-mono text-[10px] tracking-institutional uppercase text-rsv-bone">{overline}</span>
    </div>
  );
}

/* =================================================================
   HOME
================================================================= */
function Home() {
  const [directiva, setDirectiva] = useState([]);
  const [eventos, setEventos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/directiva`).then((r) => setDirectiva(r.data)).catch(console.error);
    axios.get(`${API}/eventos`).then((r) => setEventos(r.data)).catch(console.error);
  }, []);

  const lazTitular = directiva.find((m) => m.cargo_en && m.cargo_en.startsWith("LAZ"));

  return (
    <div className="bg-rsv-charcoal text-rsv-ivory min-h-screen">
      <Header />
      <main>
        <Hero />
        <SectionClub />
        <SectionFilosofia />
        <SectionCrianza />
        <SectionRazas />
        <SectionCamadas />
        <SectionDirectiva directiva={directiva} />
        <SectionLAZ titularFoto={lazTitular?.foto_url} />
        <SectionEventos eventos={eventos} />
        <SectionGaleria />
        <SectionContacto />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
